import React, { useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";

import { runVisualUnderstandingAssembler } from "../../lib/brandStudioApi";

export default function VisualUnderstandingAssemblerOwnerPanelV1() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      setResult(await runVisualUnderstandingAssembler());
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail
          ? detail.code
          : typeof detail === "string"
            ? detail
            : "";
      setError(code || "Не удалось собрать VU5.0 deterministic understanding.");
    } finally {
      setBusy(false);
    }
  };

  const report = result?.assembly_report;

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#6F7855] bg-[#F8F8EC] p-5 md:p-7"
      data-testid="visual-understanding-assembler-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#6B7352]">
        OWNER QA · VU5.0
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Deterministic Understanding Assembler
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Проверяет только целостность Source Truth и VU3 cache. Raw atoms в QA больше
        не показываются.
      </p>

      <button
        type="button"
        disabled={busy}
        onClick={run}
        className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12px] text-[#FFF8ED] disabled:opacity-50"
        data-testid="visual-understanding-assembler-run-v1"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Проверяю VU5.0…
          </>
        ) : (
          <>
            <ShieldCheck size={14} />
            Проверить assembler
          </>
        )}
      </button>

      {error && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}

      {result && report && (
        <div className="mt-6 border-t border-[#D9DCC7] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8EBD5] px-3 py-1.5 text-[10.5px] text-[#56603E]">
              <Check size={12} /> VU5.0 собран
            </span>
            <span className="text-[10.5px] text-[#796B58]">
              {report.mode} · {report.assembler_version}
            </span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <article className="rounded-[14px] border border-[#DAD5C6] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#8B765A]">
                References
              </div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                Всего: {report.reference_total}<br />
                analyzed: {report.references_analyzed}<br />
                failed: {report.references_failed}<br />
                skipped: {report.references_skipped}
              </div>
            </article>
            <article className="rounded-[14px] border border-[#DAD5C6] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#8B765A]">
                Integrity
              </div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                evidence retained: {report.raw_atom_count}<br />
                dropped: {report.dropped_items?.length || 0}<br />
                quarantined: {report.quarantined_items?.length || 0}
              </div>
            </article>
            <article className="rounded-[14px] border border-[#DAD5C6] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#8B765A]">
                Groups
              </div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                present: {(report.groups_present || []).join(", ") || "—"}<br />
                absent: {(report.groups_absent || []).join(", ") || "—"}
              </div>
            </article>
          </div>

          {(result.explicit_decisions || []).length > 0 && (
            <div className="mt-4 rounded-[14px] border border-[#DAD5C6] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#8B765A]">
                Explicit decisions · Source Truth
              </div>
              <div className="mt-2 space-y-2">
                {result.explicit_decisions.map((item) => (
                  <div key={item.id}>
                    <div className="text-[11.5px] leading-5 text-[#4C392B]">
                      • {item.value}
                    </div>
                    <div className="text-[9.5px] leading-4 text-[#8A745E]">
                      {item.origin} · {item.strength} · group: {item.group || "unresolved"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 rounded-[13px] border border-[#DAD5C6] bg-white px-4 py-3 text-[10.5px] leading-5 text-[#6D604F]">
            Raw evidence сохранён внутри VU5 и доступен provenance-слою, но больше не
            выводится на экран.
          </div>
        </div>
      )}
    </section>
  );
}
